import { ReactTable2Column, ReactTable2Table } from "./features";
import React, { useEffect } from "react";
import { keyBy } from "lodash";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ListFilter as FilterIcon } from "lucide-react";
import { useSearchParams } from "wouter";
import { useQueryState } from "../use-query-state";
import { useQueryStateArray } from "./useQueryStateArray";
import { FilterOption, useColumnFilterOptions } from "./useColumnFilterOptions";
import {
    BLANK_FILTER_LABEL,
    BLANK_FILTER_VALUE,
    MAX_RENDERED_FILTER_OPTIONS,
    arrIncludes,
    includesValueFormat,
    isBlankCellValue,
    multiSelect,
    arrayMultiSelect,
    notEquals,
    notZero,
    numberEqualOrGreaterThan,
    numberStartsWith,
    startsWith,
    toFilterOptionKey,
    toFilterOptionLabel,
    zeroFilter
} from "./filterFns";

// Re-export the row-level filter predicates and constants so existing consumers that import them from
// this module (e.g. `import { notEquals } from ".../ReactTableFilters"`) keep working after the split.
export {
    BLANK_FILTER_LABEL,
    BLANK_FILTER_VALUE,
    MAX_RENDERED_FILTER_OPTIONS,
    arrIncludes,
    includesValueFormat,
    isBlankCellValue,
    multiSelect,
    arrayMultiSelect,
    notEquals,
    notZero,
    numberEqualOrGreaterThan,
    numberStartsWith,
    startsWith,
    toFilterOptionKey,
    toFilterOptionLabel,
    zeroFilter
};
export { useColumnFilterOptions } from "./useColumnFilterOptions";
export type { FilterOption } from "./useColumnFilterOptions";

// ---------------------------------------------------------------------------
// Declarative filter API
// ---------------------------------------------------------------------------
// A column opts into a filter either declaratively - `filter: "select"` or
// `filter: { type: "select", options: [...] }` - or by passing a render function directly (the legacy
// component style). Everything funnels through the `filterRegistry` + `Filter` renderer below.

export type FilterType = "text" | "number" | "select" | "multiSelect" | "searchableMultiSelect" | "arrayMultiSelect" | "zero" | "none";

export interface FilterOptionConfig {
    value: string;
    label?: string;
}

export interface FilterConfig {
    type: FilterType;
    options?: Array<string | FilterOptionConfig>;
}

export type FilterRenderer = (args: { column: ReactTable2Column; table: ReactTable2Table }) => React.ReactElement | null;

export type ColumnFilterSpec = FilterType | FilterConfig | FilterRenderer;

// Every column gets the Excel-style searchable multi-select unless it opts into something else.
export const DEFAULT_FILTER_TYPE: FilterType = "searchableMultiSelect";

export interface FilteredColumnDef {
    filter?: ColumnFilterSpec;
}

const normalizeOptions = (options?: Array<string | FilterOptionConfig>): FilterOption[] | undefined =>
    options?.map((option) =>
        typeof option === "string" ? { value: option, label: option } : { value: option.value, label: option.label ?? option.value }
    );

// https://tanstack.com/table/v8/docs/examples/react/filters

// Lets a column set its header and filter from within a header renderer (legacy helper, still used by
// several pages). The filter can be any ColumnFilterSpec.
export const HeaderFilter = ({ column, header, filter }: { column: ReactTable2Column; header: any; filter: ColumnFilterSpec }) => {
    (column.columnDef as any).header = header;
    (column.columnDef as any).filter = filter;
    return header;
};

// ---------------------------------------------------------------------------
// Presentational filter components
// ---------------------------------------------------------------------------

export const DefaultColumnFilter = ({ column }: { column: ReactTable2Column; table?: ReactTable2Table }): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");
    (column.columnDef as any).filterFn = "includesString";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <input
            type="text"
            className="form-input"
            value={filterValue || ""}
            onChange={(e) => {
                column.setFilterValue(e.target.value);
                setQueryValue(e.target.value || "");
            }}
            size={(column.columnDef as any).maxSize || null}
        />
    );
};

// Alias with a clearer name for the declarative "text" filter.
export const TextColumnFilter = DefaultColumnFilter;

// Numeric prefix filter: same funnel-icon trigger and dropdown as the multi-select columns, but the
// dropdown holds a single search box whose value is matched against the number's own digits, so typing
// "10.2" keeps 10.245 but not 25010.2. Better than a checkbox list for high-cardinality numeric columns
// (quantity, price, settlement amount).
export const NumberColumnFilter = ({ column }: { column: ReactTable2Column; table?: ReactTable2Table }): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [open, setOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    // Read the live URL param instead of a cached hook value. useQueryState keeps stale local state after
    // a global "clear all filters" wipes the param, which would re-apply the old value; reading the URL
    // directly means a cleared param hydrates back to empty.
    const [queryArgs, setQueryArgs] = useSearchParams();
    const urlValue = queryArgs.get(column.id) || "";
    (column.columnDef as any).filterFn = "numberStartsWith";

    // Keep the column filter in sync with the URL (initial load, shared links, and global clear).
    useEffect(() => {
        const current = (filterValue as string) || "";
        if (urlValue !== current) {
            column.setFilterValue(urlValue || undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlValue]);

    const isBlankSelected = filterValue === BLANK_FILTER_VALUE;
    const isActive = typeof filterValue === "string" && filterValue !== "";

    const setValue = (value: string) => {
        column.setFilterValue(value || undefined);
        setQueryArgs(
            (prev) => {
                if (value) {
                    prev.set(column.id, value);
                } else {
                    prev.delete(column.id);
                }
                return prev;
            },
            { replace: true }
        );
    };

    return (
        <DropdownMenu.Root
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    triggerRef.current?.blur();
                }
            }}
        >
            <DropdownMenu.Trigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    aria-label="Filter"
                    title={isActive ? `Filtered (${isBlankSelected ? BLANK_FILTER_LABEL : filterValue})` : "Filter"}
                    className="inline-flex w-full min-w-9 items-center justify-end gap-0.5 self-stretch rounded pl-2 pr-1 hover:bg-gray-100"
                    style={{ background: "transparent", border: "none", cursor: "pointer", lineHeight: 0 }}
                >
                    <FilterIcon
                        size={10}
                        className={isActive ? "text-blue-600" : "text-gray-400"}
                        fill={isActive ? "currentColor" : "none"}
                    />
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="min-w-[220px] bg-white rounded-md shadow-lg border border-gray-200 p-2 z-50"
                    sideOffset={4}
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-1 pb-2">
                        <input
                            type="text"
                            inputMode="decimal"
                            className="form-input w-full"
                            placeholder="Search..."
                            value={isBlankSelected ? "" : filterValue || ""}
                            disabled={isBlankSelected}
                            autoFocus
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    </div>
                    <label className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isBlankSelected}
                            onChange={(e) => setValue(e.target.checked ? BLANK_FILTER_VALUE : "")}
                        />
                        <span className="flex-1">{BLANK_FILTER_LABEL}</span>
                    </label>
                    <div className="flex items-center justify-end gap-2 px-2 pt-1 mt-1 border-t border-gray-200">
                        <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                            disabled={!isActive}
                            onClick={() => setValue("")}
                        >
                            Clear
                        </button>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

export const SelectColumnFilter = ({
    column,
    options: providedOptions
}: {
    column: ReactTable2Column;
    table?: ReactTable2Table;
    options?: Array<string | FilterOptionConfig>;
}): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");
    const facetedOptions = useColumnFilterOptions(column);
    const options = React.useMemo<FilterOption[]>(
        () => normalizeOptions(providedOptions) ?? facetedOptions.filter((option) => option.value !== BLANK_FILTER_VALUE),
        [providedOptions, facetedOptions]
    );
    (column.columnDef as any).filterFn = "equals";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            className="custom-select form-select"
            value={filterValue ?? ""}
            onChange={(e) => {
                column.setFilterValue(e.target.value || undefined);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            {options.map((option) => (
                <option key={"key" + option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
};

export const SelectNotZeroColumnFilter = ({ column }: { column: ReactTable2Column; table?: ReactTable2Table }): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");
    (column.columnDef as any).filterFn = "zeroFilter";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            className="custom-select"
            value={filterValue ?? ""}
            onChange={(e) => {
                column.setFilterValue(e.target.value || undefined);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            <option value="0">Exclude 0</option>
            <option value="only0">Only 0</option>
        </select>
    );
};

// Shared Excel-style multi-select column filter. Fully controlled by column.getFilterValue() so every
// toggle reads the latest filter state (no internal selection that can drift and blank the table on the
// second pick). When `searchable` is set, a text box narrows the checkbox list - handy for columns with
// many distinct values - while row filtering still happens by the checked values.
export const MultiSelectFilterBase = ({
    column,
    table,
    searchable,
    filterFn = "multiSelect"
}: {
    column: ReactTable2Column;
    table?: ReactTable2Table;
    searchable: boolean;
    filterFn?: "multiSelect" | "arrayMultiSelect";
}): React.ReactElement => {
    const selected = ((column.getFilterValue() as string[]) || []).map((value) => String(value));
    // Empty default so we never auto-seed the URL from the live selection; that would fight a global
    // "clear all filters" by re-appending values right after they're removed. The hydrate effect below
    // handles URL -> filter (initial load / shared links); toggle/clear write both together.
    const [queryValues, setQueryValues] = useQueryStateArray(column.id, []);
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    (column.columnDef as any).filterFn = filterFn;

    const options = useColumnFilterOptions(column, table, { flattenArrays: filterFn === "arrayMultiSelect" });

    const visibleOptions = React.useMemo(() => {
        if (!search) {
            return options;
        }
        const needle = search.toLowerCase();
        return options.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, search]);

    // Only render a capped slice to keep the DOM small (and the dropdown fast) for huge columns.
    const renderedOptions = React.useMemo(() => visibleOptions.slice(0, MAX_RENDERED_FILTER_OPTIONS), [visibleOptions]);
    const hiddenOptionCount = visibleOptions.length - renderedOptions.length;

    // The URL query params are the source of truth: keep the column filter in sync with them (initial
    // load, shared links, and a global "clear all filters" that wipes the params). Depend only on
    // queryValues - not `column`, which TanStack recreates on every state change and would otherwise
    // re-apply stale values right after a reset.
    useEffect(() => {
        const current = ((column.getFilterValue() as string[]) || []).map((value) => String(value));
        if (JSON.stringify(queryValues) !== JSON.stringify(current)) {
            column.setFilterValue(queryValues.length ? queryValues : undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryValues]);

    const toggle = (value: string) => {
        const current = ((column.getFilterValue() as string[]) || []).map((v) => String(v));
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        column.setFilterValue(next.length ? next : undefined);
        setQueryValues(next);
    };

    const selectAll = () => {
        // Select every currently visible option (respects an active search), merged with existing picks.
        const current = ((column.getFilterValue() as string[]) || []).map((v) => String(v));
        const next = Array.from(new Set([...current, ...visibleOptions.map((option) => option.value)]));
        column.setFilterValue(next.length ? next : undefined);
        setQueryValues(next);
    };

    const clear = () => {
        column.setFilterValue(undefined);
        setQueryValues([]);
    };

    const isActive = selected.length > 0;
    const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every((option) => selected.includes(option.value));

    const onOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearch("");
            triggerRef.current?.blur();
        }
    };

    return (
        <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
            <DropdownMenu.Trigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    aria-label="Filter"
                    title={isActive ? `Filtered (${selected.length})` : "Filter"}
                    className="inline-flex w-full min-w-9 items-center justify-end gap-0.5 self-stretch rounded pl-2 pr-1 hover:bg-gray-100"
                    style={{ background: "transparent", border: "none", cursor: "pointer", lineHeight: 0 }}
                >
                    <FilterIcon
                        size={10}
                        className={isActive ? "text-blue-600" : "text-gray-400"}
                        fill={isActive ? "currentColor" : "none"}
                    />
                    {isActive ? <span className="text-[10px] font-medium leading-none text-blue-600">{selected.length}</span> : null}
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="min-w-[220px] bg-white rounded-md shadow-lg border border-gray-200 p-2 z-50"
                    sideOffset={4}
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                >
                    {searchable ? (
                        <div className="px-1 pb-2">
                            <input
                                type="text"
                                className="form-input w-full"
                                placeholder="Search..."
                                value={search}
                                autoFocus
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    // Enter acts like "Select all": pick every currently matched option and close.
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        selectAll();
                                        onOpenChange(false);
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                    <div className="flex items-center justify-between gap-2 px-2 pb-2 mb-1 border-b border-gray-200">
                        <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                            disabled={allVisibleSelected}
                            onClick={selectAll}
                        >
                            Select all
                        </button>
                        <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                            disabled={selected.length === 0}
                            onClick={clear}
                        >
                            Clear
                        </button>
                    </div>
                    <div className="max-h-60 overflow-auto pr-1">
                        {visibleOptions.length === 0 ? (
                            <div className="px-2 py-1 text-sm text-gray-400">No matches</div>
                        ) : (
                            renderedOptions.map((option) => {
                                const isChecked = selected.includes(option.value);
                                return (
                                    <DropdownMenu.CheckboxItem
                                        key={option.value}
                                        className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-default"
                                        checked={isChecked}
                                        onCheckedChange={() => toggle(option.value)}
                                        onSelect={(e) => e.preventDefault()}
                                    >
                                        <div
                                            className="border border-gray-300 rounded bg-white"
                                            style={{ width: 16, height: 16, position: "relative", lineHeight: 0, flex: "0 0 auto" }}
                                        >
                                            {isChecked ? (
                                                <Check
                                                    width={10}
                                                    height={8}
                                                    strokeWidth={2}
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        display: "block",
                                                        pointerEvents: "none"
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                        <span className="flex-1">{option.label}</span>
                                    </DropdownMenu.CheckboxItem>
                                );
                            })
                        )}
                        {hiddenOptionCount > 0 ? (
                            <div className="px-2 py-1 text-xs text-gray-400">
                                Showing first {renderedOptions.length} of {visibleOptions.length}. Type to search…
                            </div>
                        ) : null}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

// Multi-select column filter - pick several faceted values at once (no search box).
export const MultiSelectColumnFilter = ({ column, table }: { column: ReactTable2Column; table?: ReactTable2Table }): React.ReactElement => (
    <MultiSelectFilterBase column={column} table={table} searchable={false} />
);

// Searchable multi-select column filter - type to narrow the checkbox list, then pick several values.
export const SearchableMultiSelectColumnFilter = ({
    column,
    table
}: {
    column: ReactTable2Column;
    table?: ReactTable2Table;
}): React.ReactElement => <MultiSelectFilterBase column={column} table={table} searchable={true} />;

// ---------------------------------------------------------------------------
// Legacy closure-style filters (kept for backwards compatibility)
// ---------------------------------------------------------------------------

// Closure: single-select over the values of an enum.
export const SelectColumnFilterType = (enumType: Record<string, string>): FilterRenderer => {
    const component = ({ column }: { column: ReactTable2Column; table: ReactTable2Table }): React.ReactElement => (
        <SelectColumnFilter column={column} options={Object.values(enumType)} />
    );
    (component as any).filterFn = "equals";
    return component;
};

// Closure: single-select over a list of { _id, name } where the label is the name and the filter value
// is the _id (arrIncludes against an array-of-ids column).
export const SelectColumnFilterArrayOfIds = (lookup: { _id: string; name: string }[]): FilterRenderer => {
    const byName = keyBy(lookup, "name");
    const optionNames = Object.keys(byName).sort();
    const options: FilterOptionConfig[] = optionNames.map((name) => ({ value: byName[name]._id, label: name }));

    const component = ({ column }: { column: ReactTable2Column; table: ReactTable2Table }): React.ReactElement => {
        (column.columnDef as any).filterFn = "arrIncludes";
        return <SelectColumnFilter column={column} options={options} />;
    };
    (component as any).filterFn = "arrIncludes";
    return component;
};

// Single-select over the distinct members of array-valued cells (arrIncludes).
export const SelectColumnFilterArrayOfStrings = ({
    column
}: {
    column: ReactTable2Column;
    table?: ReactTable2Table;
}): React.ReactElement => {
    const filterValue: any = column.getFilterValue();
    const [queryValue, setQueryValue] = useQueryState(column.id, filterValue || "");

    const options = React.useMemo(() => {
        const values = Array.from(column.getFacetedUniqueValues().keys());
        const uniqueValues = values.reduce((acc: Set<any>, value: any) => {
            (value || []).forEach((x: any) => acc.add(x));
            return acc;
        }, new Set());
        return Array.from(uniqueValues).sort();
    }, [column]);

    (column.columnDef as any).filterFn = "arrIncludes";

    useEffect(() => {
        if (queryValue !== filterValue && queryValue !== "") {
            column.setFilterValue(queryValue);
        }
    }, [filterValue, queryValue, column]);

    return (
        <select
            className="custom-select"
            value={filterValue ?? ""}
            onChange={(e) => {
                column.setFilterValue(e.target.value || undefined);
                setQueryValue(e.target.value || "");
            }}
        >
            <option value="">All</option>
            {options.map((optionName: any) => (
                <option key={optionName} value={optionName}>
                    {optionName}
                </option>
            ))}
        </select>
    );
};

// Expose the filterFn name so ReactTable can set it on the column def up front. The render-time
// columnDef.filterFn mutation is lost when a parent recreates its column defs (e.g. a columns useMemo
// depending on queryArgs), and the row model is computed before filter components re-render.
(DefaultColumnFilter as any).filterFn = "includesString";
(TextColumnFilter as any).filterFn = "includesString";
(NumberColumnFilter as any).filterFn = "numberStartsWith";
(SelectColumnFilter as any).filterFn = "equals";
(SelectNotZeroColumnFilter as any).filterFn = "zeroFilter";
(SelectColumnFilterArrayOfStrings as any).filterFn = "arrIncludes";
(MultiSelectColumnFilter as any).filterFn = "multiSelect";
(SearchableMultiSelectColumnFilter as any).filterFn = "multiSelect";

// ---------------------------------------------------------------------------
// Registry + renderer
// ---------------------------------------------------------------------------

interface RegistryEntry {
    filterFn?: string;
    render: (args: { column: ReactTable2Column; table: ReactTable2Table; config?: FilterConfig }) => React.ReactElement | null;
}

const filterRegistry: Record<FilterType, RegistryEntry> = {
    text: { filterFn: "includesString", render: ({ column, table }) => <DefaultColumnFilter column={column} table={table} /> },
    number: { filterFn: "numberStartsWith", render: ({ column, table }) => <NumberColumnFilter column={column} table={table} /> },
    select: {
        filterFn: "equals",
        render: ({ column, table, config }) => <SelectColumnFilter column={column} table={table} options={config?.options} />
    },
    multiSelect: {
        filterFn: "multiSelect",
        render: ({ column, table }) => <MultiSelectFilterBase column={column} table={table} searchable={false} />
    },
    searchableMultiSelect: {
        filterFn: "multiSelect",
        render: ({ column, table }) => <MultiSelectFilterBase column={column} table={table} searchable={true} />
    },
    arrayMultiSelect: {
        filterFn: "arrayMultiSelect",
        render: ({ column, table }) => <MultiSelectFilterBase column={column} table={table} searchable={true} filterFn="arrayMultiSelect" />
    },
    zero: { filterFn: "zeroFilter", render: ({ column, table }) => <SelectNotZeroColumnFilter column={column} table={table} /> },
    none: { render: () => null }
};

// Resolve the filterFn *name* for a column's filter spec without rendering it. ReactTable uses this to
// set columnDef.filterFn up front so the row model always filters with the right function.
export const resolveFilterFnName = (spec?: ColumnFilterSpec): string | undefined => {
    const resolved = (spec ?? DEFAULT_FILTER_TYPE) as ColumnFilterSpec;
    if (typeof resolved === "function") {
        return (resolved as any).filterFn;
    }
    if (typeof resolved === "string") {
        return filterRegistry[resolved]?.filterFn;
    }
    if (resolved && typeof resolved === "object" && "type" in resolved) {
        return filterRegistry[resolved.type]?.filterFn;
    }
    return undefined;
};

// Renders the filter control for a column header. Accepts the declarative spec (string / config object)
// or a legacy render function; falls back to the default searchable multi-select.
export const Filter = ({ column, table }: { column: ReactTable2Column; table: ReactTable2Table }): React.ReactElement => {
    const spec = ((column.columnDef as any).filter ?? DEFAULT_FILTER_TYPE) as ColumnFilterSpec;

    let content: React.ReactElement | null = null;
    if (typeof spec === "function") {
        content = spec({ column, table });
    } else if (typeof spec === "string") {
        content = filterRegistry[spec]?.render({ column, table }) ?? null;
    } else if (spec && typeof spec === "object" && "type" in spec) {
        content = filterRegistry[spec.type]?.render({ column, table, config: spec }) ?? null;
    }

    return <>{content}</>;
};
